import { isEqual, isNil } from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'react-use';
import { type LanguagePackSpecsFragment } from '../../../api/fragments/LanguagePackSpecs';
import { LanguageContext, type LanguageContextData } from './Context';

export type LanguageContextManagerProps = {
    children: JSX.Element | React.ReactNode;
    languages?: Pick<LanguagePackSpecsFragment, 'code' | 'displayName' | 'id'>[];
};

const DEFAULT_LANGUAGE_ID = 'languageId';

const getLanguageFromStorage = (storageKey: string, fallback: string = null): string | undefined => {
    if (typeof window === 'undefined') {
        return fallback;
    }

    const result = window.sessionStorage.getItem(storageKey);
    if (!result) {
        return fallback;
    }

    return JSON.parse(result);
};

export const LanguageContextManager = ({ children, languages }: LanguageContextManagerProps) => {
    const { i18n } = useTranslation();

    if (!isNil(languages) && languages?.length > 0) {
        const [languageOptions, defaultLanguage] = useMemo(() => {
            const languageOptions = languages
                .map(language => ({
                    value: language.id,
                    label: language.displayName,
                    code: language.code,
                }))
                .toSorted((a, b) => a.label.localeCompare(b.label));
            const defaultLanguage = languages[0];

            return [languageOptions, defaultLanguage];
        }, [languages]);

        const [currentLanguageId, setCurrentLanguageId] = useSessionStorage<string | null>(
            DEFAULT_LANGUAGE_ID,
            getLanguageFromStorage(DEFAULT_LANGUAGE_ID, languages[0].id)
        );

        useEffect(() => {
            const defaultLanguageId = defaultLanguage.id;

            const newLanguage = languageOptions.find(language => language.value === currentLanguageId);

            if (!isNil(newLanguage)) {
                i18n.changeLanguage(newLanguage.value);
                if (!isEqual(defaultLanguageId, currentLanguageId)) {
                    setCurrentLanguageId(newLanguage.value);
                }
            } else if (defaultLanguageId && isNil(newLanguage)) {
                i18n.changeLanguage(defaultLanguageId);
                setCurrentLanguageId(defaultLanguageId);
            }
        }, [currentLanguageId, defaultLanguage.id, i18n, languageOptions, setCurrentLanguageId]);

        const context = useMemo(
            (): LanguageContextData => ({
                currentLanguageId,
                languageOptions,
                setCurrentLanguageId,
            }),
            [currentLanguageId, languageOptions, setCurrentLanguageId]
        );

        return <LanguageContext.Provider value={context}>{children}</LanguageContext.Provider>;
    }

    return <LanguageContext.Provider value={null}>{children}</LanguageContext.Provider>;
};
